import { useMemo } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { OrgUnitEmployeeRole } from '~/data/orgunits/types';
import { useMyUnitIds } from '~/data/orgunits/useMyUnitIds';

import { OrganizationUnit } from './types';
import { useAllOrgUnits } from './useAllOrgUnits';

export const useMyUnits = (
  includeMyIndirectTeams?: boolean,
): {
  myUnits: OrganizationUnit[];
  isLoading: boolean;
  hasData: boolean;
} => {
  const {
    orgUnits: allUnits,
    isLoading: isLoadingAllUnits,
    hasData: hasAllUnitsData,
  } = useAllOrgUnits();
  const myId = useLoggedInEmployeeId();
  const {
    aMyManUnits,
    aMyHRUnits,
    isLoading: isLoadingMyUnits,
    hasData: hasMyUnitIdsData,
  } = useMyUnitIds();

  const isLoading = isLoadingAllUnits || isLoadingMyUnits;
  const hasData = hasAllUnitsData && hasMyUnitIdsData;

  const myUnits: OrganizationUnit[] = useMemo(() => {
    if (!allUnits.length || !hasData) {
      return [];
    }

    const indirectTeams = includeMyIndirectTeams
      ? allUnits
          .filter((unit) =>
            unit.fOrgUnitResponsibleEmps.some(
              (emp) =>
                [
                  OrgUnitEmployeeRole.DeputyManager,
                  OrgUnitEmployeeRole.AlternateManager,
                  OrgUnitEmployeeRole.HRAuditor,
                  OrgUnitEmployeeRole.HRDev,
                  OrgUnitEmployeeRole.HRSalary,
                  OrgUnitEmployeeRole.HRStaff,
                  OrgUnitEmployeeRole.ITAdmin,
                ].includes(emp.fUnitRole) && emp.fEmployeeId === myId,
            ),
          )
          .map((unit) => unit.fOrgUnitId)
      : [];

    const allManagedUnits = [...aMyManUnits, ...aMyHRUnits, ...indirectTeams];

    const allUniqueManagedUnits = allManagedUnits.filter(
      (unit, index) => allManagedUnits.indexOf(unit) === index,
    );

    return !!allUniqueManagedUnits.length
      ? allUnits.filter((unit) =>
          allUniqueManagedUnits.includes(unit.fOrgUnitId),
        )
      : [];
  }, [
    allUnits,
    aMyManUnits,
    aMyHRUnits,
    includeMyIndirectTeams,
    myId,
    hasData,
  ]);

  return { myUnits, isLoading, hasData };
};
